import { termMonths } from "../lib/utils";
import { useState, useEffect, useMemo } from "react";
import {
  getClients, createClient, getUsers, createMigratedOrder, uploadFile, API_BASE,
} from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import {
  Loader2, Plus, Package, FileText, X as XIcon, Paperclip, ClipboardEdit,
} from "lucide-react";

const PRODUCT_OPTIONS = [
  { id: "marketing", label: "Marketing" },
  { id: "web_design", label: "Web Design" },
  { id: "seo_aeo", label: "SEO / AEO" },
  { id: "prospect_tools", label: "ProspectID / ProspectMax" },
];

const EMPTY_CLIENT = {
  company: "",
  website: "",
  industry: "",
  name: "",
  email: "",
  phone: "",
};

const EMPTY_ORDER = {
  client_id: "",
  rep_id: "",
  product_types: [],
  sub_products: [],
  start_date: "",
  end_date: "",
  month_to_month: false,
  monthly_amount: "",
  contract_value_type: "wholesale",
  notes: "",
  file_ids: [],
};

/**
 * Super-admin-only dialog for backfilling an existing live order into the
 * portal so the rep can start using messages/attachments even though the
 * order was booked outside/before onboarding.
 *
 * Props:
 *   open (bool), onOpenChange (fn), onCreated (fn) — refresh caller state
 */
export default function AddMigratedOrderDialog({ open, onOpenChange, onCreated }) {
  const [clients, setClients] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clientMode, setClientMode] = useState("existing");  // "existing" | "new"
  const [newClient, setNewClient] = useState({ ...EMPTY_CLIENT });
  const [form, setForm] = useState({ ...EMPTY_ORDER });
  const [uploadedFiles, setUploadedFiles] = useState([]);  // [{id, filename}]
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([getClients(), getUsers()])
      .then(([cRes, uRes]) => {
        setClients(cRes.data || []);
        // Only reps + managers can be assigned to an order
        setReps((uRes.data || []).filter((u) => ["rep", "manager"].includes(u.role)));
      })
      .catch(() => toast.error("Could not load clients/reps"))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) {
      // Reset form on close
      setForm({ ...EMPTY_ORDER });
      setNewClient({ ...EMPTY_CLIENT });
      setClientMode("existing");
      setUploadedFiles([]);
    }
  }, [open]);

  const toggleProduct = (id) => {
    setForm((f) => {
      const has = f.product_types.includes(id);
      return { ...f, product_types: has ? f.product_types.filter((t) => t !== id) : [...f.product_types, id] };
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await uploadFile(file);
      setUploadedFiles((prev) => [...prev, data]);
      setForm((f) => ({ ...f, file_ids: [...f.file_ids, data.id] }));
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = (fileId) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
    setForm((f) => ({ ...f, file_ids: f.file_ids.filter((id) => id !== fileId) }));
  };

  const derivedContractValue = useMemo(() => {
    const monthly = parseFloat(form.monthly_amount);
    if (Number.isNaN(monthly) || monthly <= 0) return null;
    if (form.month_to_month) return null;
    if (!form.start_date || !form.end_date) return null;
    const months = termMonths(form.start_date, form.end_date);
    if (!months) return null;
    return (monthly * months).toFixed(2);
  }, [form.monthly_amount, form.start_date, form.end_date, form.month_to_month]);

  const canSubmit = useMemo(() => {
    const clientOk = clientMode === "existing" ? !!form.client_id : (newClient.company.trim() && newClient.email.trim());
    return (
      clientOk &&
      !!form.rep_id &&
      form.product_types.length > 0 &&
      !!parseFloat(form.monthly_amount) &&
      (form.month_to_month || (form.start_date && form.end_date))
    );
  }, [clientMode, form, newClient]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Resolve client_id — create the client first if in "new" mode
      let clientId = form.client_id;
      if (clientMode === "new") {
        // Super-admin creating a client must pass agency_id. Derive it from
        // the rep they just picked (every rep belongs to an agency).
        const pickedRep = reps.find((r) => r.id === form.rep_id);
        const payload = {
          ...newClient,
          agency_id: pickedRep?.agency_id || undefined,
          rep_id: form.rep_id || undefined,
        };
        const { data } = await createClient(payload);
        clientId = data.id;
      }

      const payload = {
        client_id: clientId,
        rep_id: form.rep_id,
        product_types: form.product_types,
        sub_products: form.sub_products,
        notes: form.notes || null,
        start_date: form.month_to_month ? null : (form.start_date || null),
        end_date: form.month_to_month ? null : (form.end_date || null),
        monthly_amount: parseFloat(form.monthly_amount) || null,
        contract_value_type: form.contract_value_type,
        auto_renew: form.month_to_month,
        file_ids: form.file_ids,
      };
      const { data: proj } = await createMigratedOrder(payload);
      toast.success(`Migrated order created for ${proj.client_company || proj.client_name}`);
      if (onCreated) onCreated(proj);
      onOpenChange(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : Array.isArray(d) ? d.map((e) => `${(e?.loc || []).slice(-1)[0] || "field"}: ${e?.msg || e}`).join("; ")
        : (err?.message || "Could not create migrated order");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-migrated-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardEdit className="h-5 w-5 text-fuchsia-600" />
            Add Monthly Active Client
          </DialogTitle>
          <DialogDescription>
            For clients whose recurring work started outside the portal. Skips onboarding + verification
            and lands them directly in Monthly Active. Silent — no email notifications sent.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {/* Client selection */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Client</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={clientMode === "existing" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setClientMode("existing")}
                  data-testid="client-mode-existing"
                >
                  Existing Client
                </Button>
                <Button
                  type="button"
                  variant={clientMode === "new" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setClientMode("new")}
                  data-testid="client-mode-new"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> New Client
                </Button>
              </div>

              {clientMode === "existing" ? (
                <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
                  <SelectTrigger data-testid="migrated-client-select">
                    <SelectValue placeholder="Pick a client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company || c.name} {c.rep_name ? `· ${c.rep_name}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="grid grid-cols-2 gap-3 p-3 border rounded-md bg-secondary/20">
                  <div className="col-span-2">
                    <Label className="text-xs">Company *</Label>
                    <Input
                      value={newClient.company}
                      onChange={(e) => setNewClient({ ...newClient, company: e.target.value })}
                      placeholder="Company name"
                      data-testid="new-client-company"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Contact name</Label>
                    <Input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} data-testid="new-client-name" />
                  </div>
                  <div>
                    <Label className="text-xs">Email *</Label>
                    <Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} data-testid="new-client-email" />
                  </div>
                  <div>
                    <Label className="text-xs">Website</Label>
                    <Input value={newClient.website} onChange={(e) => setNewClient({ ...newClient, website: e.target.value })} data-testid="new-client-website" />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} data-testid="new-client-phone" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Industry</Label>
                    <Input value={newClient.industry} onChange={(e) => setNewClient({ ...newClient, industry: e.target.value })} data-testid="new-client-industry" />
                  </div>
                </div>
              )}
            </div>

            {/* Rep assignment */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Assign to Rep</Label>
              <Select value={form.rep_id} onValueChange={(v) => setForm((f) => ({ ...f, rep_id: v }))}>
                <SelectTrigger data-testid="migrated-rep-select">
                  <SelectValue placeholder="Pick the rep who owns this order..." />
                </SelectTrigger>
                <SelectContent>
                  {reps.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} · {u.role === "manager" ? "Manager" : "Rep"} {u.agency_name ? `· ${u.agency_name}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Product picker */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-1">
                <Package className="h-4 w-4" /> Product(s) Purchased
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {PRODUCT_OPTIONS.map((p) => (
                  <label
                    key={p.id}
                    className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-sm ${form.product_types.includes(p.id) ? "border-primary bg-primary/5" : "border-zinc-200"}`}
                    data-testid={`migrated-product-${p.id}`}
                  >
                    <Checkbox
                      checked={form.product_types.includes(p.id)}
                      onCheckedChange={() => toggleProduct(p.id)}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Term */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Campaign Term</Label>
              <div className="flex gap-2 mb-2">
                <Button
                  type="button"
                  variant={!form.month_to_month ? "default" : "outline"}
                  size="sm"
                  onClick={() => setForm((f) => ({ ...f, month_to_month: false }))}
                >
                  Fixed Term (Start - End)
                </Button>
                <Button
                  type="button"
                  variant={form.month_to_month ? "default" : "outline"}
                  size="sm"
                  onClick={() => setForm((f) => ({ ...f, month_to_month: true }))}
                >
                  Month-to-Month
                </Button>
              </div>
              {!form.month_to_month && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Start Date</Label>
                    <Input
                      type="date"
                      value={form.start_date}
                      onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                      data-testid="migrated-start-date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">End Date</Label>
                    <Input
                      type="date"
                      value={form.end_date}
                      onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                      data-testid="migrated-end-date"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Amount */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Monthly Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="$ per month"
                  value={form.monthly_amount}
                  onChange={(e) => setForm((f) => ({ ...f, monthly_amount: e.target.value }))}
                  data-testid="migrated-monthly-amount"
                />
                {derivedContractValue && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Total Contract Value (auto-derived): <b>${derivedContractValue}</b>
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.contract_value_type} onValueChange={(v) => setForm((f) => ({ ...f, contract_value_type: v }))}>
                  <SelectTrigger data-testid="migrated-value-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wholesale">Wholesale</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-1">
                <Paperclip className="h-4 w-4" /> Attachments (contract, past deliverables)
              </Label>
              <input
                id="migrated-file-upload"
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                data-testid="migrated-file-input"
              />
              <label
                htmlFor="migrated-file-upload"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-dashed rounded cursor-pointer text-xs text-muted-foreground hover:bg-secondary/40"
              >
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                {uploading ? "Uploading..." : "Add file"}
              </label>
              {uploadedFiles.length > 0 && (
                <div className="space-y-1">
                  {uploadedFiles.map((f) => (
                    <div key={f.id} className="flex items-center justify-between text-xs bg-secondary/40 px-2 py-1 rounded">
                      <span className="flex items-center gap-1.5 truncate">
                        <FileText className="h-3 w-3 shrink-0" />
                        <a href={`${API_BASE}/api/files/${f.id}/download`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">
                          {f.filename}
                        </a>
                      </span>
                      <button type="button" onClick={() => removeFile(f.id)} className="text-muted-foreground hover:text-destructive">
                        <XIcon className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Any context about this order, original booking source, etc."
                data-testid="migrated-notes"
              />
            </div>

            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">Migrated</Badge>
              <p className="text-xs text-amber-900">
                Order will land in <b>Monthly Active Clients</b> with a "Migrated" pill so the team knows it bypassed onboarding.
                Rep can start messaging you about this client immediately.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="migrated-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            data-testid="migrated-submit"
          >
            {submitting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Creating...</> : "Create Migrated Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
